import { fontNeeds, loadFonts } from '../fonts/load';
import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { assets } from '../editor/storage';
import { ThaiTextbox } from '../thai/ThaiTextbox';
import { isTable } from '../table/tableFabric';
import { $ } from './dom';
import { closeSheets } from './sheets';
import type { ObjectActions } from './objectActions';
import { toast } from './toast';

export const LABEL: Record<string, string> = {
  thaiTextbox: 'ข้อความ', rect: 'สี่เหลี่ยม', circle: 'วงกลม', triangle: 'สามเหลี่ยม', line: 'เส้น', image: 'รูปภาพ',
  polygon: 'รูปหลายเหลี่ยม', path: 'เส้นวาด', group: 'กราฟิก'
};
/** what an object is called (a group is a กราฟิก, or a ตาราง when it carries a table — src/table/tableFabric.ts) */
export const kindName = (o: fabric.Object | null | undefined): string | undefined => isTable(o) ? 'ตาราง' : LABEL[o?.type ?? ''];

export function makeText(text: string, opt: fabric.ITextboxOptions = {}) {
  return new ThaiTextbox(text, { fontFamily: 'Sarabun', fontSize: 64, fill: '#1F2A1C', width: 620, lineHeight: 1.35, ...opt });
}

/** a new shape's look (also the drawing tools' shapes, src/ui/drawTools.ts) */
export const shapeBase = { strokeWidth: 0, stroke: '#1A1E26', strokeUniform: true };
export const SHAPE_FILL = '#6B3FD9';

/** Load a web font weight before measuring text with it; reports a failure instead of silently using a fallback. */
async function ensureFont(family: string, weight: number) {
  const failed = await loadFonts(fontNeeds([{ type: 'thaiTextbox', fontFamily: family, fontWeight: weight }]));
  if (failed.length) toast(`โหลดฟอนต์ ${family} ไม่สำเร็จ ตรวจอินเทอร์เน็ตแล้วลองใหม่ ตอนนี้แสดงด้วยฟอนต์สำรอง`, true);
}

/** On phones a panel covers the work, so close it after adding something. */
const afterAdd = () => { if (!matchMedia('(min-width: 900px)').matches) closeSheets(); };

/** `actions`: duplicate/delete/lock/copy/paste shared with the object menu (src/ui/objectActions.ts) */
export function initObjectTools(ed: Editor, history: History, actions: ObjectActions) {
  const { canvas } = ed;
  const W = () => ed.doc.w * 0.8;

  /* ---------- text panel ---------- */
  async function addText(text: string, family: string, weight: number, size: number) {
    await ensureFont(family, weight);
    const u = ed.unit();
    ed.place(makeText(text, { fontFamily: family, fontWeight: weight, fontSize: size * u, width: W() }));
    afterAdd();
  }
  $('xBox').onclick = () => addText('แตะสองครั้งเพื่อแก้ข้อความ', 'Sarabun', 400, 56);
  $('xHead').onclick = () => addText('หัวเรื่อง', 'Kanit', 700, 96);
  $('xSub').onclick = () => addText('หัวเรื่องรอง', 'Kanit', 600, 64);
  $('xBody').onclick = () => addText('ข้อความเนื้อหา', 'Sarabun', 400, 40);

  async function addPair(head: string, family: string, weight: number) {
    await Promise.all([ensureFont(family, weight), ensureFont('Sarabun', 400)]);
    const u = ed.unit();
    const h = makeText(head, { fontFamily: family, fontWeight: weight, fontSize: 88 * u, width: W(), fill: '#1D1A2B' });
    const b = makeText('รายละเอียดสั้น ๆ ใต้หัวเรื่อง', { fontFamily: 'Sarabun', fontSize: 40 * u, width: W(), fill: '#3C3850' });
    const left = (ed.doc.w - W()) / 2;
    const top = (ed.doc.h - (h.height ?? 0) - (b.height ?? 0) - 24 * u) / 2;
    h.set({ left, top }); b.set({ left, top: top + (h.height ?? 0) + 24 * u });
    h.setCoords(); b.setCoords();
    canvas.add(h, b);
    canvas.setActiveObject(h); ed.paint();
    afterAdd();
  }
  $('xPair1').onclick = () => addPair('ลดราคา 20%', 'Prompt', 600);
  $('xPair2').onclick = () => addPair('สินค้าใหม่', 'Mitr', 500);

  /* ---------- elements panel ---------- */
  const add = (o: fabric.Object) => { ed.place(o); afterAdd(); };
  $('eRect').onclick = () => { const u = ed.unit(); add(new fabric.Rect({ width: 360 * u, height: 240 * u, rx: 0, ry: 0, fill: SHAPE_FILL, ...shapeBase })); };
  $('eRound').onclick = () => { const u = ed.unit(); add(new fabric.Rect({ width: 360 * u, height: 240 * u, rx: 36 * u, ry: 36 * u, fill: SHAPE_FILL, ...shapeBase })); };
  $('eCircle').onclick = () => { const u = ed.unit(); add(new fabric.Circle({ radius: 150 * u, fill: SHAPE_FILL, ...shapeBase })); };
  $('eTri').onclick = () => { const u = ed.unit(); add(new fabric.Triangle({ width: 300 * u, height: 260 * u, fill: SHAPE_FILL, ...shapeBase })); };
  $('eLine').onclick = () => { const u = ed.unit(); add(new fabric.Line([0, 0, 400 * u, 0], { stroke: '#1D1A2B', strokeWidth: 8 * u, strokeUniform: true, strokeLineCap: 'round' })); };
  $('eDiag').onclick = () => { const u = ed.unit(); add(new fabric.Line([0, 300 * u, 300 * u, 0], { stroke: '#1D1A2B', strokeWidth: 8 * u, strokeUniform: true, strokeLineCap: 'round' })); };

  /* ---------- upload panel ---------- */
  const file = $<HTMLInputElement>('file');
  $('uPick').onclick = () => file.click();
  file.onchange = () => {
    const f = file.files?.[0];
    file.value = '';
    if (!f) return;
    if (!/^image\//.test(f.type)) { toast(`ไฟล์ "${f.name}" ไม่ใช่รูปภาพ เลือกไฟล์ JPG, PNG หรือ WebP`, true); return; }
    const url = URL.createObjectURL(f);
    fabric.Image.fromURL(url, img => {
      if (!img || !img.width) {
        URL.revokeObjectURL(url);
        toast(`เปิดรูป "${f.name}" ไม่ได้ เบราว์เซอร์อาจไม่รองรับไฟล์ชนิดนี้ (เช่น HEIC) ลองแปลงเป็น JPG ก่อน`, true);
        return;
      }
      assets.set(url, f);
      const fitTo = Math.min(ed.doc.w, ed.doc.h) * 0.7;
      img.scale(Math.min(1, fitTo / Math.max(img.width, img.height ?? 0)));
      img.set({ name: f.name.replace(/\.[^.]+$/, '').slice(0, 28) || 'รูปภาพ' });
      add(img);
    });
  };

  /* ---------- selection actions (floating bar + keyboard; the same ones as the object menu) ---------- */
  /** a reason from an action is shown, never dropped */
  const say = (why: string | null) => { if (why) toast(why, true); };
  $('sDup').onclick = () => say(actions.duplicate());
  $('sDel').onclick = () => say(actions.remove());
  $('sLock').onclick = () => say(actions.lock());
  $('sGroup').onclick = () => say(actions.groupState() === 'group' ? actions.group() : actions.ungroup());
  $('zoomChip').onclick = () => ed.fit();

  document.addEventListener('keydown', e => {
    const o = canvas.getActiveObject();
    if (o && (o as fabric.IText).isEditing) return;
    if (/INPUT|SELECT|TEXTAREA/.test((document.activeElement as HTMLElement | null)?.tagName ?? '')) return;
    const mod = e.ctrlKey || e.metaKey, k = e.key.toLowerCase();
    if (mod && k === 'z') { e.preventDefault(); e.shiftKey ? history.redo() : history.undo(); }
    else if (mod && k === 'y') { e.preventDefault(); history.redo(); }
    else if (mod && k === 'd') { e.preventDefault(); say(actions.duplicate()); }
    // ⌘G จัดกลุ่ม · ⇧⌘G ยกเลิกการรวมกลุ่ม
    else if (mod && k === 'g') { e.preventDefault(); say(e.shiftKey ? actions.ungroup() : actions.group()); }
    // ⌘C: the chosen objects — unless some words on the page are selected (then the browser copies them)
    else if (mod && k === 'c' && o && !window.getSelection()?.toString()) { e.preventDefault(); say(actions.copy()); }
    else if (mod && k === 'v' && !e.repeat) { e.preventDefault(); void actions.paste().then(say, err => say(String(err instanceof Error ? err.message : err))); }
    else if ((e.key === 'Delete' || e.key === 'Backspace') && o) { e.preventDefault(); say(actions.remove()); }
    else if (e.key === 'Escape' && o) { canvas.discardActiveObject(); canvas.requestRenderAll(); }
  });
}
